import { useCallback, useEffect, useRef, useState } from 'react';

import { CLIENT_ID, getAccessToken } from '@/utils/http';
import * as api from './api';
import { WikiCommentThread } from './types';

interface RealtimeEvent {
  type: string;
  board_id: string;
  client_id?: string | null;
  payload?: unknown;
}

const wsUrl = (room: string, actorId: string, token: string | null) => {
  const base = window.config.apiUrl.replace(/^http/, 'ws');
  const u = new URL(`${base}/ws`);
  u.searchParams.set('board_id', room);
  u.searchParams.set('client_id', CLIENT_ID);
  if (actorId) u.searchParams.set('actor_id', actorId);
  if (token) u.searchParams.set('access_token', token);
  return u.toString();
};

/** Holds a page's comment threads live: an initial GET + a socket on "wiki-page:<id>" applying
 *  wiki_comment.upsert / .delete (skipping our own echoes). Exposes optimistic mutators the rail/editor
 *  call; the socket echo reconciles. */
export function useWikiComments(pageId: string, selfId: string) {
  const [threads, setThreads] = useState<WikiCommentThread[]>([]);
  const [loaded, setLoaded] = useState(false);

  const upsert = useCallback((t: WikiCommentThread) => {
    setThreads((prev) => {
      const i = prev.findIndex((x) => x.id === t.id);
      if (i === -1) return [t, ...prev];
      const next = prev.slice();
      next[i] = t;
      return next;
    });
  }, []);
  const remove = useCallback((ids: string[]) => {
    const set = new Set(ids);
    setThreads((prev) => prev.filter((t) => !set.has(t.id)));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    api
      .listWikiComments(pageId)
      .then((ts) => {
        if (!cancelled) {
          setThreads(ts);
          setLoaded(true);
        }
      })
      .catch(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [pageId]);

  const upsertRef = useRef(upsert);
  upsertRef.current = upsert;
  const removeRef = useRef(remove);
  removeRef.current = remove;

  useEffect(() => {
    const room = `wiki-page:${pageId}`;
    let ws: WebSocket | null = null;
    let closedByUs = false;
    let retry = 0;
    let timer: number | undefined;
    const connect = () => {
      ws = new WebSocket(wsUrl(room, selfId, getAccessToken()));
      ws.onopen = () => {
        retry = 0;
      };
      ws.onmessage = (e) => {
        let ev: RealtimeEvent;
        try {
          ev = JSON.parse(e.data as string);
        } catch {
          return;
        }
        if (ev.client_id && ev.client_id === CLIENT_ID) return;
        if (ev.type === 'wiki_comment.upsert') {
          const t = ev.payload as WikiCommentThread;
          if (t?.id) upsertRef.current(t);
        } else if (ev.type === 'wiki_comment.delete') {
          const ids = (ev.payload as { ids?: string[] })?.ids ?? [];
          if (ids.length) removeRef.current(ids);
        }
      };
      ws.onclose = () => {
        if (closedByUs) return;
        retry += 1;
        timer = window.setTimeout(connect, Math.min(1000 * 2 ** Math.min(retry, 4), 15000));
      };
      ws.onerror = () => ws?.close();
    };
    connect();
    return () => {
      closedByUs = true;
      if (timer) window.clearTimeout(timer);
      ws?.close();
    };
  }, [pageId, selfId]);

  return { threads, loaded, upsert, remove };
}
